import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Calendar, momentLocalizer} from "react-big-calendar";
import moment from "moment";
import "moment/locale/ru";
import MainLayout from "../layout/MainLayout";
import {getHRAvailability, createAvailability, deleteAvailability} from "../services/sheduleApi";
import {useAlertContext} from "../context/AlertContext";
import {useAuth} from "../context/AuthContext";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./CalendarPage.css";
import "../components/calendar/CalendarDialogs.css";
import ExerciseModal from "../components/calendar/ExerciseModal";
import BookingModal from "../components/calendar/BookingModal";
import {getCandidate} from "../services/candidateApi";
import {getEvents} from "../services/eventsApi";
import EventTypeFilters, {ALL_EVENT_FILTERS} from "../components/events/EventTypeFilters";
import EventDetailsModal from "../components/events/EventDetailsModal";
import {filterEventsByCategories, getEventFilterCategory, getEventTypeMeta, isBirthdayEvent} from "../components/events/eventTypes";

moment.locale("ru");
const localizer = momentLocalizer(moment);
const VIEW_ORDER = ["year", "month", "week", "day"];

export function getCalendarRange(date, view) {
    const anchor = moment(date);
    if (view === "year") {
        return {start: anchor.clone().startOf("year").format("YYYY-MM-DD"), end: anchor.clone().endOf("year").format("YYYY-MM-DD")};
    }
    if (view === "month") {
        return {start: anchor.clone().startOf("month").startOf("week").format("YYYY-MM-DD"), end: anchor.clone().endOf("month").endOf("week").format("YYYY-MM-DD")};
    }
    return {start: anchor.clone().startOf(view).format("YYYY-MM-DD"), end: anchor.clone().endOf(view).format("YYYY-MM-DD")};
}

function slotToEvent(slot) {
    const name = slot.candidate_name;
    return {
        id: `avail-${slot.id}`,
        title: slot.is_booked ? (name || "Занято") : "Доступен",
        start: new Date(`${slot.date}T${slot.start_time}`),
        end: new Date(`${slot.date}T${slot.end_time}`),
        type: slot.is_booked ? "interview" : "other",
        calendarKind: slot.is_booked ? "booked" : "availability",
        resource: {...slot, candidate: slot.candidate_id ? {id: slot.candidate_id, full_name: name || `Кандидат #${slot.candidate_id}`} : null},
    };
}

export function plannerEventToCalendarEvent(event) {
    const day = moment(event?.event_date, "YYYY-MM-DD", true);
    if (!day.isValid()) return null;
    const meta = getEventTypeMeta(event.type);
    const subject = event.relative_name || event.child_name || event.employee_name;
    const title = subject ? `${meta.label}: ${subject}` : (event.note || meta.label);
    return {
        id: `event-${event.id}`,
        title,
        start: day.startOf("day").toDate(),
        end: day.clone().add(1, "day").startOf("day").toDate(),
        allDay: true,
        type: event.type || "other",
        calendarKind: "event",
        resource: event,
    };
}

function Icon({name}) {
    const paths = {
        previous: <path d="m15 18-6-6 6-6"/>, next: <path d="m9 18 6-6-6-6"/>, minus: <path d="M5 12h14"/>,
        plus: <><path d="M12 5v14"/><path d="M5 12h14"/></>,
        refresh: <><path d="M20 11a8 8 0 1 0-2.34 5.66"/><path d="M20 4v7h-7"/></>,
    };
    return <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{paths[name]}</svg>;
}

export function CalendarEvent({event}) {
    const birthday = isBirthdayEvent(event.type);
    const timeLabel = `${moment(event.start).format("HH:mm")}–${moment(event.end).format("HH:mm")}`;
    return (
        <div className="calendar-event-content" title={birthday ? event.title : `${timeLabel} · ${event.title}`}>
            {!birthday && <span className="calendar-event-time">{timeLabel}</span>}
            <span className="calendar-event-title">{event.title}</span>
        </div>
    );
}

function YearView({date, events, onSelectMonth, onSelectDay}) {
    const eventsByDay = useMemo(() => events.reduce((result, event) => {
        const key = moment(event.start).format("YYYY-MM-DD");
        result[key] = result[key] || [];
        result[key].push(event);
        return result;
    }, {}), [events]);
    const today = moment().format("YYYY-MM-DD");

    return (
        <div className="calendar-year-grid" aria-label={`Календарь на ${moment(date).format("YYYY")} год`}>
            {Array.from({length: 12}, (_, monthIndex) => {
                const month = moment(date).month(monthIndex).startOf("month");
                const gridStart = month.clone().startOf("week");
                const days = Array.from({length: 42}, (__, index) => gridStart.clone().add(index, "day"));
                const monthEvents = events.filter((event) => {
                    const eventDate = moment(event.start);
                    return eventDate.year() === month.year() && eventDate.month() === monthIndex;
                });
                return (
                    <section className="calendar-mini-month" key={monthIndex}>
                        <button type="button" className="calendar-mini-title" onClick={() => onSelectMonth(month.toDate())}>
                            <span>{month.format("MMMM")}</span>
                            {monthEvents.length > 0 && <span className="calendar-mini-count">{monthEvents.length}</span>}
                        </button>
                        <div className="calendar-mini-weekdays" aria-hidden="true">
                            {moment.weekdaysMin(true).map((day) => <span key={day}>{day}</span>)}
                        </div>
                        <div className="calendar-mini-days">
                            {days.map((day) => {
                                const key = day.format("YYYY-MM-DD");
                                const dayEvents = eventsByDay[key] || [];
                                const hasAvailable = dayEvents.some((event) => event.type === "availability");
                                const hasBooked = dayEvents.some((event) => event.type === "booked");
                                return (
                                    <button type="button" key={key}
                                        className={["calendar-mini-day", day.month() !== monthIndex ? "is-outside" : "", key === today ? "is-today" : "", dayEvents.length ? "has-events" : ""].join(" ")}
                                        onClick={() => onSelectDay(day.toDate())}
                                        aria-label={`${day.format("D MMMM YYYY")}${dayEvents.length ? `, событий: ${dayEvents.length}` : ""}`}>
                                        <span>{day.format("D")}</span>
                                        {(hasAvailable || hasBooked) && <span className="calendar-mini-markers" aria-hidden="true">{hasAvailable && <i className="is-available"/>}{hasBooked && <i className="is-booked"/>}</span>}
                                        {dayEvents.length > 1 && <span className="calendar-mini-event-count" aria-hidden="true">{dayEvents.length}</span>}
                                    </button>
                                );
                            })}
                        </div>
                    </section>
                );
            })}
        </div>
    );
}

export default function CalendarPage() {
    const [availabilityEvents, setAvailabilityEvents] = useState([]);
    const [plannerEvents, setPlannerEvents] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState(ALL_EVENT_FILTERS);
    const [loading, setLoading] = useState(false);
    const [view, setView] = useState("month");
    const [calendarDate, setCalendarDate] = useState(() => new Date());
    const currentRange = useMemo(() => getCalendarRange(calendarDate, view), [calendarDate, view]);
    const [deleteModal, setDeleteModal] = useState({isOpen: false, slot: null});
    const [createModal, setCreateModal] = useState({isOpen: false, start: null, end: null});
    const {showAlert} = useAlertContext();
    const {user} = useAuth();
    const userId = user?.erp_user_id || user?.id;
    const [exerciseModal, setExerciseModal] = useState({isOpen: false, candidate: null});
    const [bookingModal, setBookingModal] = useState({isOpen: false, slot: null});
    const [selectedPlannerEvent, setSelectedPlannerEvent] = useState(null);
    const [creating, setCreating] = useState(false);
    const fetchSeq = useRef(0);
    const abortRef = useRef(null);
    const events = useMemo(() => [
        ...availabilityEvents,
        ...plannerEvents.filter((event) => !event.is_done).map(plannerEventToCalendarEvent).filter(Boolean),
    ], [availabilityEvents, plannerEvents]);
    const filteredEvents = useMemo(
        () => filterEventsByCategories(events, selectedCategories),
        [events, selectedCategories]
    );

    const fetchData = useCallback(async () => {
        const seq = ++fetchSeq.current;
        abortRef.current?.abort();
        abortRef.current = new AbortController();
        const signal = abortRef.current.signal;
        setLoading(true);
        try {
            const [availabilityResult, eventsResult] = await Promise.allSettled([
                userId ? getHRAvailability(userId, currentRange.start, currentRange.end, {signal}) : Promise.resolve([]),
                getEvents({signal}),
            ]);
            if (seq !== fetchSeq.current || signal.aborted) return;
            if (availabilityResult.status === "fulfilled") {
                setAvailabilityEvents((availabilityResult.value || []).map(slotToEvent));
            } else {
                showAlert("Не удалось загрузить слоты доступности. События календаря доступны отдельно.", "warning");
            }
            if (eventsResult.status === "fulfilled") {
                setPlannerEvents(Array.isArray(eventsResult.value) ? eventsResult.value : []);
            } else {
                showAlert("Не удалось загрузить события календаря. Попробуйте обновить страницу.", "error");
            }
        } catch (e) {
            if (e?.name === "AbortError" || signal.aborted || seq !== fetchSeq.current) return;
            console.error("Error fetching calendar", e);
            showAlert(`Ошибка загрузки календаря: ${e?.message || String(e)}`, "error");
        } finally { if (seq === fetchSeq.current) setLoading(false); }
    }, [userId, showAlert, currentRange]);

    useEffect(() => {
        const timer = setTimeout(fetchData, 80);
        return () => { clearTimeout(timer); abortRef.current?.abort(); };
    }, [fetchData]);

    const handleSelectEvent = async (event) => {
        if (event.calendarKind === "booked") {
            let candidate = event.resource.candidate;
            if (candidate?.id) {
                try { const full = await getCandidate(candidate.id); if (full && typeof full === "object") candidate = {...candidate, ...full}; }
                catch (_) { /* The modal can continue with the candidate stub. */ }
            }
            if (!candidate?.id) { showAlert("Кандидат не найден для этого слота", "warning"); return; }
            setExerciseModal({isOpen: true, candidate});
        } else if (event.calendarKind === "availability") {
            setBookingModal({isOpen: true, slot: event.resource});
        } else if (event.calendarKind === "event") {
            setSelectedPlannerEvent(event.resource);
        }
    };

    const confirmCreate = async () => {
        if (creating) return;
        try {
            if (!userId) return showAlert("Нет HR user id — перелогиньтесь", "error");
            if (!createModal.start || !createModal.end) return showAlert("Не выбран интервал времени", "error");
            const start = moment(createModal.start); const end = moment(createModal.end);
            if (!start.isValid() || !end.isValid()) return showAlert("Некорректная дата/время слота", "error");
            if (!end.isAfter(start)) return showAlert("Конец интервала должен быть позже начала", "error");
            if (end.diff(start, "minutes") < 60) return showAlert("Минимальная длина слота — 60 минут", "error");
            setCreating(true);
            const created = await createAvailability({hr_id: String(userId).trim(), date: start.format("YYYY-MM-DD"), start_time: start.format("HH:mm:ss"), end_time: end.format("HH:mm:ss"), slot_length_minutes: 60});
            setCreateModal({isOpen: false, start: null, end: null});
            const createdList = Array.isArray(created) ? created : [];
            if (createdList.length) setAvailabilityEvents((previous) => {
                const mapped = createdList.map(slotToEvent); const ids = new Set(mapped.map((event) => event.id));
                return [...mapped, ...previous.filter((event) => !ids.has(event.id))];
            });
            showAlert(createdList.length ? `Доступность добавлена (${createdList.length})` : "Доступность добавлена", "success");
            await fetchData();
        } catch (e) { showAlert(`Ошибка создания слота: ${e.message}`, "error"); }
        finally { setCreating(false); }
    };

    const confirmDelete = async () => {
        try { await deleteAvailability(deleteModal.slot.id); showAlert("Доступность удалена", "success"); setDeleteModal({isOpen: false, slot: null}); await fetchData(); }
        catch (e) { showAlert(`Ошибка удаления: ${e.message}`, "error"); }
    };

    const changePeriod = (direction) => {
        const unit = view === "year" ? "year" : view === "month" ? "month" : view === "week" ? "week" : "day";
        setCalendarDate(moment(calendarDate).add(direction, unit).toDate());
    };
    const setZoom = (direction) => {
        const index = Math.max(0, Math.min(VIEW_ORDER.length - 1, VIEW_ORDER.indexOf(view) + direction));
        setView(VIEW_ORDER[index]);
    };
    const title = view === "year" ? moment(calendarDate).format("YYYY") : view === "month" ? moment(calendarDate).format("MMMM YYYY") : view === "week" ? `${moment(calendarDate).startOf("week").format("D MMM")} — ${moment(calendarDate).endOf("week").format("D MMM YYYY")}` : moment(calendarDate).format("D MMMM YYYY");
    const eventStyleGetter = (event) => {
        const category = getEventFilterCategory(event.type);
        const backgroundColor = event.calendarKind === "availability"
            ? "#2f9e77"
            : category === "birthdays"
                ? "#8b5cf6"
                : category === "interviews"
                    ? "#dc5f5f"
                    : "#64748b";
        return {style: {backgroundColor, borderRadius: "6px", color: "white", border: "none"}};
    };

    return (
        <MainLayout className="p-4 sm:p-6">
            <div className="calendar-page">
                <header className="calendar-page-header">
                    <div><p className="calendar-eyebrow">РАСПИСАНИЕ HR</p><h1>Календарь</h1><p>Слоты, собеседования, дни рождения и другие события</p></div>
                    <div className="calendar-legend" aria-label="Обозначения событий"><span><i className="is-available"/>Доступно</span><span><i className="is-booked"/>Занято</span></div>
                </header>
                <div className="calendar-filters">
                    <EventTypeFilters events={events} selected={selectedCategories} onChange={setSelectedCategories}/>
                </div>
                <section className="calendar-shell">
                    <div className="calendar-toolbar">
                        <div className="calendar-period-controls">
                            <button type="button" className="calendar-today" onClick={() => setCalendarDate(new Date())}>Сегодня</button>
                            <button type="button" className="calendar-icon-button" onClick={() => changePeriod(-1)} aria-label="Предыдущий период"><Icon name="previous"/></button>
                            <button type="button" className="calendar-icon-button" onClick={() => changePeriod(1)} aria-label="Следующий период"><Icon name="next"/></button>
                            <h2>{title}</h2>
                        </div>
                        <div className="calendar-view-controls">
                            <div className="calendar-view-tabs" role="group" aria-label="Масштаб календаря">
                                {[["year", "Год"], ["month", "Месяц"], ["week", "Неделя"], ["day", "День"]].map(([value, label]) => <button type="button" key={value} className={view === value ? "is-active" : ""} aria-pressed={view === value} onClick={() => setView(value)}>{label}</button>)}
                            </div>
                            <div className="calendar-zoom-controls" role="group" aria-label="Изменить масштаб">
                                <button type="button" onClick={() => setZoom(-1)} disabled={view === "year"} aria-label="Уменьшить масштаб"><Icon name="minus"/></button>
                                <button type="button" onClick={() => setZoom(1)} disabled={view === "day"} aria-label="Увеличить масштаб"><Icon name="plus"/></button>
                            </div>
                            <button type="button" className="calendar-icon-button" onClick={fetchData} disabled={loading || !userId} aria-label="Обновить календарь"><Icon name="refresh"/></button>
                        </div>
                    </div>
                    <div className={`calendar-stage calendar-stage--${view}`} aria-busy={loading}>
                        {loading && <div className="calendar-loading"><span/>Обновляем календарь…</div>}
                        {view === "year" ? <YearView date={calendarDate} events={filteredEvents} onSelectMonth={(date) => {setCalendarDate(date); setView("month");}} onSelectDay={(date) => {setCalendarDate(date); setView("day");}}/> : (
                            <Calendar localizer={localizer} culture="ru" date={calendarDate} view={view} toolbar={false} events={filteredEvents}
                                startAccessor="start" endAccessor="end" selectable={view === "week" || view === "day"}
                                onSelectSlot={({start, end}) => setCreateModal({isOpen: true, start, end})} onSelectEvent={handleSelectEvent}
                                onNavigate={setCalendarDate} onView={setView} onDrillDown={(date) => {setCalendarDate(date); setView("day");}}
                                drilldownView="day" eventPropGetter={eventStyleGetter} views={["month", "week", "day"]}
                                components={{event: CalendarEvent}}
                                messages={{today: "Сегодня", previous: "Назад", next: "Вперёд", month: "Месяц", week: "Неделя", day: "День", agenda: "Повестка", date: "Дата", time: "Время", event: "Событие", noEventsInRange: "Нет событий в этом диапазоне", showMore: (total) => `Ещё ${total}`}}
                                formats={{weekdayFormat: (date) => moment(date).format("dd")}} step={60} timeslots={1}
                                min={new Date(0, 0, 0, 8, 0, 0)} max={new Date(0, 0, 0, 20, 0, 0)}/>
                        )}
                    </div>
                </section>
            </div>

            {deleteModal.isOpen && <div className="calendar-dialog-backdrop" role="presentation"><div className="calendar-dialog calendar-dialog--danger" role="dialog" aria-modal="true" aria-labelledby="delete-slot-title">
                <div className="calendar-dialog-header"><div><span className="calendar-dialog-kicker">ДОСТУПНОСТЬ</span><h3 id="delete-slot-title">Удалить слот?</h3><p>После удаления кандидаты не смогут выбрать это время.</p></div><button type="button" className="calendar-dialog-close" onClick={() => setDeleteModal({isOpen: false, slot: null})} aria-label="Закрыть">×</button></div>
                <div className="calendar-dialog-body"><div className="calendar-slot-summary"><div><span>Дата</span><strong>{moment(deleteModal.slot?.date).format("D MMMM YYYY")}</strong></div><div><span>Время</span><strong>{String(deleteModal.slot?.start_time || "").slice(0, 5)}–{String(deleteModal.slot?.end_time || "").slice(0, 5)}</strong></div></div></div>
                <div className="calendar-dialog-footer"><button onClick={() => setDeleteModal({isOpen: false, slot: null})} className="calendar-button calendar-button--secondary">Оставить слот</button><button onClick={confirmDelete} className="calendar-button calendar-button--danger">Удалить слот</button></div>
            </div></div>}
            {createModal.isOpen && <div className="calendar-dialog-backdrop" role="presentation"><div className="calendar-dialog" role="dialog" aria-modal="true" aria-labelledby="create-slot-title">
                <div className="calendar-dialog-header"><div><span className="calendar-dialog-kicker">НОВАЯ ДОСТУПНОСТЬ</span><h3 id="create-slot-title">Открыть время для встречи</h3><p>Слот появится в календаре и станет доступен для бронирования.</p></div><button type="button" className="calendar-dialog-close" onClick={() => setCreateModal({isOpen: false, start: null, end: null})} disabled={creating} aria-label="Закрыть">×</button></div>
                <div className="calendar-dialog-body"><div className="calendar-slot-summary"><div><span>Дата</span><strong>{moment(createModal.start).format("D MMMM YYYY")}</strong></div><div><span>Время</span><strong>{moment(createModal.start).format("HH:mm")}–{moment(createModal.end).format("HH:mm")}</strong></div></div><p className="calendar-dialog-note">Интервал будет разделён на часовые слоты, если выбран период длиннее одного часа.</p></div>
                <div className="calendar-dialog-footer"><button type="button" onClick={() => setCreateModal({isOpen: false, start: null, end: null})} disabled={creating} className="calendar-button calendar-button--secondary">Отмена</button><button type="button" onClick={confirmCreate} disabled={creating} className="calendar-button calendar-button--primary">{creating ? "Создаём…" : "Открыть слот"}</button></div>
            </div></div>}
            {exerciseModal.isOpen && exerciseModal.candidate && <ExerciseModal candidate={exerciseModal.candidate} onClose={() => setExerciseModal({isOpen: false, candidate: null})}/>}
            {bookingModal.isOpen && bookingModal.slot && <BookingModal slot={bookingModal.slot} onClose={() => setBookingModal({isOpen: false, slot: null})} onSuccess={fetchData} onDeleteSlot={(slot) => {setBookingModal({isOpen: false, slot: null}); setDeleteModal({isOpen: true, slot});}}/>}
            <EventDetailsModal event={selectedPlannerEvent} onClose={() => setSelectedPlannerEvent(null)}/>
        </MainLayout>
    );
}
